import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { UploadCloud, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

/**
 * Drag-drop uploader — returns a public URL via onUploaded(url, meta).
 * Props:
 *   kind: "ad_creative" | "signage_media" | ...
 *   accept: comma-separated extensions (e.g., "image/*,video/*")
 *   currentUrl: preview
 *   label: button text
 */
export default function DropUpload({
  kind = "generic",
  accept = "image/*,video/*",
  currentUrl,
  onUploaded,
  label = "Drop file here or click to browse",
  testid = "drop-upload",
}) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const [pct, setPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setPct(0);
    try {
      const { data } = await api.post(`/uploads?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setPreview(url);
      onUploaded?.(url, data);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setPct(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };

  const clear = () => {
    setPreview(null);
    onUploaded?.("", null);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => !busy && !preview && ref.current?.click()}
      data-testid={testid}
      className={`relative rounded-2xl border-2 border-dashed p-6 transition cursor-pointer ${
        drag
          ? "border-[#D4B878] bg-[#D4B878]/5"
          : "border-white/15 bg-white/[0.02] hover:border-[#D4B878]/50"
      }`}
    >
      <input
        type="file"
        ref={ref}
        accept={accept}
        onChange={onPick}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {busy && (
        <div className="flex flex-col items-center gap-3 py-8">
          <Loader2 size={28} className="animate-spin text-[#E8D6A3]" />
          <div className="text-sm text-zinc-400">Uploading… {pct}%</div>
          <div className="h-1.5 w-56 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4B878] to-[#E8D6A3] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
      {!busy && preview && (
        <div className="relative">
          {preview.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
            <video src={preview} controls className="w-full max-h-64 rounded-xl bg-black" />
          ) : (
            <img src={preview} alt="" className="w-full max-h-64 object-contain rounded-xl bg-black" />
          )}
          <div className="flex items-center justify-between mt-3">
            <div className="inline-flex items-center gap-2 text-xs text-emerald-400">
              <Check size={14} /> Uploaded
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  ref.current?.click();
                }}
                data-testid={`${testid}-replace`}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
                data-testid={`${testid}-clear`}
                className="text-xs text-zinc-400 hover:text-red-400"
              >
                <X size={12} className="inline" /> Remove
              </button>
            </div>
          </div>
        </div>
      )}
      {!busy && !preview && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <div className="p-3 rounded-xl bg-[#D4B878]/10 text-[#E8D6A3]">
            <UploadCloud size={24} />
          </div>
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-zinc-500">Up to 60MB · PNG/JPG/WEBP/MP4/WEBM</div>
        </div>
      )}
    </div>
  );
}
